import { Space, Center, Box, Group, Checkbox, SegmentedControl, Tooltip, ThemeIcon, MultiSelect } from '@mantine/core';
import { useContext, useEffect, useState } from 'react';
import { fetchRecommendableContacts } from '../../../Service/restapi/contactGroupService';
import { WindowContext } from '../../../Service/Context/WindowContext';
import CreateEventIcon from '../../../Organismns/Eventlist/CreateEventIcon';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import { EventsContext } from '../../../Service/Context/EventsContext';
import { DataS } from '../../../Service/DataS';
import Contact from '../../../Types/Contact';
import BookingStateItem from '../BookingStateItem';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import IntroductionTour from '../../../Organismns/Tour/IntroductionTour';
import { ThemeText, ThemeTitle } from '../../../Atoms/ThemeText';
import EventSearchAutocomplete from '../../../Organismns/Event/EventSearchAutocompleteV2';
import ListBookingStateSelection from './ListBookingStateSelection';
import DateIntervalSelection from './DateIntervalSelection';
import AvailabilitySelection from './AvailabilitySelection';
import ExternalCalendarsSelection from './ExternalCalendarsSelection';
import EventKindSelection from './EventKindSelection';
import { IconCloudDownload } from '@tabler/icons';
import { openExportEventDatesModal } from '../../Event/EventDateModals';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { EventS } from '../../../Service/EventS';

const EventListNavBar = () => {
  const { sm, windowHeight } = useContext(WindowContext);
  const { licence } = useJwtClaims();
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { filter, updateFilter, view, setView, query, setQuery } = useContext(EventsContext);
  const {
    agencyModeActive,
    sorting,
    lastContactShare,
    filterCustomerContactShared,
    recommendationContacts,
    showAppointments,
  } = filter;
  const [contacts, setContacts] = useState<Contact[]>([]);
  const contactOptions: any = [...DataS.getContactsOptions(contacts)];
  const { profile } = useContext<any>(ProfileContext);
  const [showIntroductionTour, setShowIntroductionTour] = useState<boolean>(false);
  const [datelessEventCount, setDatelessEventCount] = useState(0);
  const showLicenceHeader = licence && (licence === 'BASIC' || licence === 'INTERACTIVE_TRIAL_30DAYS');

  useEffect(() => {
    EventS.fetchDatelessEventsCount().then(setDatelessEventCount);
    fetchRecommendableContacts().then((resp) => {
      if (resp.ok) {
        resp.json().then((contacts) => {
          setContacts(contacts);
        });
      }
    });
  }, []);

  return (
    <div>
      {showIntroductionTour && <IntroductionTour />}
      <div
        style={{
          height: windowHeight - (sm ? 140 : 80 + (showLicenceHeader ? 40 : 0)),
          overflow: 'auto',
        }}
      >
        {!sm && (
          <Group p="sm" position="apart" sx={{ alignItems: 'center' }}>
            <ThemeTitle order={3}>Events</ThemeTitle>
            <Group spacing="xs">
              <Tooltip label="Export als CSV" transition="slide-down">
                <ThemeIcon
                  size={20}
                  style={{ cursor: 'pointer' }}
                  variant="gradient"
                  gradient={{ from: '#647c91', to: '#a2a7b0', deg: 135 }}
                  sx={{ marginRight: 3 }}
                >
                  <IconCloudDownload color="white" onClick={openExportEventDatesModal} />
                </ThemeIcon>
              </Tooltip>
              <CreateEventIcon onClickCallback={() => setShowIntroductionTour(false)} />
            </Group>
          </Group>
        )}
        {view === 'list' && sorting !== 'createdAt' && (
          <Box p="xs" mt={5}>
            <EventSearchAutocomplete />
          </Box>
        )}
        {agencyModeActive && view === 'list' ? (
          <>
            <Box p="xs">
              <MultiSelect
                multiple
                variant="filled"
                searchable
                clearable
                placeholder="Dienstleister filtern"
                value={recommendationContacts}
                data={contactOptions}
                onChange={(val) => updateFilter({ recommendationContacts: val })}
              />
            </Box>
            <Space h="md" />
            <ThemeText pl={10}>Auftragsdaten</ThemeText>
            <Space h="xs" />
            <BookingStateItem
              label="Alle"
              onClick={() => {
                updateFilter({
                  filterCustomerContactShared: !filterCustomerContactShared,
                });
                updateFilter({ lastContactShare: null });
              }}
              active={!filterCustomerContactShared}
            />
            <BookingStateItem
              label="Nur übermittelte"
              onClick={() => {
                updateFilter({ lastContactShare: true });
                updateFilter({ filterCustomerContactShared: true });
              }}
              active={lastContactShare === true}
            />
            <BookingStateItem
              label="Nur nicht übermittelte"
              onClick={() => {
                updateFilter({ lastContactShare: false });
                updateFilter({ filterCustomerContactShared: true });
              }}
              active={lastContactShare === false}
            />
          </>
        ) : (
          <>
            {profile && LicenceS.hasAccessToResourceModule(profile.id) && (
              <>
                <ThemeText mt="md" mb="xs" pl={10}>
                  Ansicht
                </ThemeText>
                <BookingStateItem label="Liste" onClick={() => setView('list')} active={view === 'list'} />
                <BookingStateItem label="Kalender" active={view === 'calendar'} onClick={() => setView('calendar')} />
                <BookingStateItem
                  label="Zeitleiste"
                  onClick={() =>
                    executeWithLicenceCheck(LicenceS.Restrictions.RESOURCE_PLANNING, () => setView('timeline'))
                  }
                  active={view === 'timeline'}
                />
              </>
            )}

            {view === 'list' && (
              <>
                <Space h="md" />
                <ThemeText pl={10}>Sortieren</ThemeText>
                <Space h="xs" />
                <BookingStateItem
                  label="Chronologisch"
                  onClick={() => updateFilter({ sorting: 'chronological' })}
                  active={sorting === 'chronological'}
                />
                <BookingStateItem
                  label="Ohne Datum"
                  onClick={() => updateFilter({ sorting: 'dateless' })}
                  active={sorting === 'dateless'}
                  count={datelessEventCount > 0 ? datelessEventCount : undefined}
                  countWeight="bold"
                />
                <BookingStateItem
                  label="Letzte Anfragen"
                  onClick={() => updateFilter({ sorting: 'createdAt' })}
                  active={sorting === 'createdAt'}
                />
              </>
            )}
          </>
        )}
        {sorting === 'chronological' && view === 'list' && <DateIntervalSelection />}
        <ListBookingStateSelection />
        <EventKindSelection />
        {!agencyModeActive && view === 'list' && (
          <>
            <ThemeText mt="xl" pl={10}>
              Optionen
            </ThemeText>
            <Checkbox
              mt="sm"
              ml="md"
              mb="md"
              checked={showAppointments}
              label="Termine anzeigen"
              onChange={() => updateFilter({ showAppointments: !showAppointments })}
            />
          </>
        )}
        {view === 'calendar' && <ExternalCalendarsSelection />}
        {agencyModeActive && <AvailabilitySelection />}
        <Space h="xl" />
      </div>
      {view === 'list' && (
        <div style={{ borderTop: '1px solid #E2E2E2' }}>
          <Center>
            <SegmentedControl
              color="blue"
              value={agencyModeActive ? 'AGENTUR' : 'SOLO'}
              onChange={(val) => {
                executeWithLicenceCheck(LicenceS.Restrictions.AGENCY_MODE, () =>
                  updateFilter({ agencyModeActive: val === 'AGENTUR' }),
                );
              }}
              data={[
                { label: 'Solo', value: 'SOLO' },
                { label: 'Agentur', value: 'AGENTUR' },
              ]}
              sx={{ position: 'absolute', bottom: 20 }}
            />
          </Center>
          <Space h="xl" />
        </div>
      )}
    </div>
  );
};

export default EventListNavBar;
